import { configurePipelineClient } from "./pipeline";
import { configureDb, getDocClient, putItem, scanAll, getItem, updateItem, Tables } from "./db";
import { configureCodeCommit } from "./commits";
import type { AuthSession, PortalRole } from "../types";

const USERS_TABLE = Tables.USERS;
const STORAGE_KEY = "devportal-auth-session";
const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || "https://csjto20hja.execute-api.us-east-1.amazonaws.com";

/**
 * Bootstrap credentials — baked into the build via env vars.
 * This user can ONLY read the pos-portal-users table.
 * Used to authenticate logins from any device.
 */
const BOOTSTRAP_CREDENTIALS = {
  accessKeyId: import.meta.env.VITE_BOOTSTRAP_ACCESS_KEY_ID || "",
  secretAccessKey: import.meta.env.VITE_BOOTSTRAP_SECRET_ACCESS_KEY || "",
};

let bootstrapped = false;

/** Ensure DB is configured with at least bootstrap credentials */
function ensureBootstrapped() {
  if (!bootstrapped && BOOTSTRAP_CREDENTIALS.accessKeyId) {
    configureDb(BOOTSTRAP_CREDENTIALS);
    bootstrapped = true;
  }
}

export function isBootstrapped(): boolean {
  return bootstrapped || !!BOOTSTRAP_CREDENTIALS.accessKeyId;
}

/**
 * Manual bootstrap — for first-time setup when env vars aren't set.
 */
export function bootstrap(credentials: {
  accessKeyId: string;
  secretAccessKey: string;
}) {
  configureDb(credentials);
  bootstrapped = true;
}

export interface PortalUser {
  username: string;
  passwordHash: string;
  role: PortalRole;
  displayName: string;
  email: string;
  accessKeyId: string;
  secretAccessKey: string;
  createdAt: string;
  lastLogin?: string;
  mustChangePassword?: boolean; // true = force password change on next login
}

/**
 * Simple hash for passwords (browser-compatible).
 * In production you'd use bcrypt via a backend Lambda,
 * but for a Tooling portal this is adequate.
 */
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Login with username and password via the Auth API.
 * No DynamoDB credentials needed in the browser.
 */
export async function login(username: string, password: string): Promise<AuthSession> {
  const resp = await fetch(`${AUTH_API_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data.error || "Authentication failed");
  }

  // Configure SDK clients with the user's IAM credentials
  const credentials = {
    accessKeyId: data.accessKeyId,
    secretAccessKey: data.secretAccessKey,
  };

  configurePipelineClient(credentials);
  configureDb(credentials);
  configureCodeCommit(credentials);
  bootstrapped = true;

  // Build session
  const session: AuthSession = {
    username: data.username,
    displayName: data.displayName,
    role: data.role,
    accessKeyId: data.accessKeyId,
    secretAccessKey: data.secretAccessKey,
    mustChangePassword: data.mustChangePassword,
  };

  // Persist session
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

  return session;
}

/** Restore session from localStorage */
export function restoreSession(): AuthSession | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    const session: AuthSession = JSON.parse(stored);

    // Reconfigure SDK clients
    const credentials = {
      accessKeyId: session.accessKeyId,
      secretAccessKey: session.secretAccessKey,
    };
    configurePipelineClient(credentials);
    configureDb(credentials);
    configureCodeCommit(credentials);
    bootstrapped = true;

    return session;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/** Clear session */
export function logout(): void {
  localStorage.removeItem(STORAGE_KEY);
  bootstrapped = false;
}

// ============================
// User Management (Admin only)
// ============================

/** Create a new portal user — auto-creates IAM user, adds to group, generates keys */
export async function createUser(
  data: {
    username: string;
    password?: string;
    role: PortalRole;
    displayName: string;
    email: string;
    sendEmail?: boolean;
  },
  onProgress?: (step: string, status: "pending" | "active" | "done" | "error") => void
): Promise<PortalUser> {
  ensureBootstrapped();

  const progress = onProgress || (() => {});

  progress("Checking username availability", "active");
  const existing = await getItem(USERS_TABLE, { username: data.username });
  if (existing) {
    progress("Checking username availability", "error");
    throw new Error(`User "${data.username}" already exists`);
  }
  progress("Checking username availability", "done");

  // Auto-create IAM user and generate keys
  const { IAMClient, CreateUserCommand, AddUserToGroupCommand, CreateAccessKeyCommand } = await import("@aws-sdk/client-iam");

  // Use the current logged-in user's credentials for IAM operations
  const currentSession = restoreSession();
  const iamCredentials = currentSession ? {
    accessKeyId: currentSession.accessKeyId,
    secretAccessKey: currentSession.secretAccessKey,
  } : undefined;
  const iamClient = new IAMClient({ region: "us-east-1", credentials: iamCredentials });

  // Create IAM user
  progress("Creating IAM user", "active");
  try {
    await iamClient.send(new CreateUserCommand({ UserName: `pos-portal-${data.username}` }));
  } catch (err: any) {
    if (err.name !== "EntityAlreadyExistsException") {
      progress("Creating IAM user", "error");
      throw err;
    }
  }
  progress("Creating IAM user", "done");

  // Add to appropriate IAM group
  progress("Assigning role permissions", "active");
  const groupMap: Record<PortalRole, string> = {
    admin: "POS-Portal-Admins",
    authorizer: "POS-Portal-Authorizers",
    user: "POS-Portal-Users",
  };

  // Retry group assignment (IAM user creation may take a moment to propagate)
  let groupAdded = false;
  for (let attempt = 0; attempt < 3 && !groupAdded; attempt++) {
    try {
      if (attempt > 0) await new Promise((r) => setTimeout(r, 2000));
      await iamClient.send(new AddUserToGroupCommand({
        UserName: `pos-portal-${data.username}`,
        GroupName: groupMap[data.role],
      }));
      groupAdded = true;
    } catch (err: any) {
      if (attempt === 2) {
        progress("Assigning role permissions", "error");
        throw new Error(`Failed to add user to group: ${err.message}`);
      }
    }
  }
  progress("Assigning role permissions", "done");

  // Generate access keys
  progress("Generating access keys", "active");
  const keyResp = await iamClient.send(new CreateAccessKeyCommand({
    UserName: `pos-portal-${data.username}`,
  }));

  if (!keyResp.AccessKey?.AccessKeyId || !keyResp.AccessKey?.SecretAccessKey) {
    progress("Generating access keys", "error");
    throw new Error("Failed to generate access keys for IAM user");
  }
  progress("Generating access keys", "done");

  // Save to DynamoDB
  progress("Saving to database", "active");
  const password = data.password || generateTempPassword();
  const mustChange = !data.password;

  const user: PortalUser = {
    username: data.username,
    passwordHash: await hashPassword(password),
    role: data.role,
    displayName: data.displayName,
    email: data.email,
    accessKeyId: keyResp.AccessKey.AccessKeyId,
    secretAccessKey: keyResp.AccessKey.SecretAccessKey,
    createdAt: new Date().toISOString(),
    mustChangePassword: mustChange,
  };

  await putItem(USERS_TABLE, user);
  progress("Saving to database", "done");

  // Send welcome email with temp password
  if (data.sendEmail && data.email && mustChange) {
    progress("Sending welcome email", "active");
    try {
      await sendTempPasswordEmail(data.email, data.username, password);
      progress("Sending welcome email", "done");
    } catch (err: any) {
      progress("Sending welcome email", "error");
      console.warn("Failed to send email:", err.message);
    }
  }

  // Wait for IAM propagation
  progress("Waiting for IAM propagation", "active");
  await new Promise((resolve) => setTimeout(resolve, 5000));
  progress("Waiting for IAM propagation", "done");

  progress("Ready", "done");
  return user;
}

/** Get all portal users (admin view) */
export async function getAllUsers(): Promise<Omit<PortalUser, "passwordHash" | "secretAccessKey">[]> {
  const items = await scanAll(USERS_TABLE);
  return (items as PortalUser[]).map(({ passwordHash, secretAccessKey, ...rest }) => rest);
}

/** Update a user's role */
export async function updateUserRole(username: string, role: PortalRole): Promise<void> {
  await updateItem(USERS_TABLE, { username }, { role });
}

/** Update a user's password */
export async function updateUserPassword(username: string, newPassword: string): Promise<void> {
  const hash = await hashPassword(newPassword);
  await updateItem(USERS_TABLE, { username }, { passwordHash: hash });
}

/** Update a user's IAM credentials */
export async function updateUserCredentials(
  username: string,
  accessKeyId: string,
  secretAccessKey: string
): Promise<void> {
  await updateItem(USERS_TABLE, { username }, { accessKeyId, secretAccessKey });
}

/** Delete a portal user — removes IAM user, keys, group membership, and DynamoDB record */
export async function deleteUser(username: string): Promise<void> {
  // Delete IAM user and resources
  const { IAMClient, DeleteAccessKeyCommand, RemoveUserFromGroupCommand, DeleteUserCommand, ListAccessKeysCommand, ListGroupsForUserCommand } = await import("@aws-sdk/client-iam");

  const currentSession = restoreSession();
  const iamCredentials = currentSession ? {
    accessKeyId: currentSession.accessKeyId,
    secretAccessKey: currentSession.secretAccessKey,
  } : undefined;
  const iamClient = new IAMClient({ region: "us-east-1", credentials: iamCredentials });
  const iamUsername = `pos-portal-${username}`;

  try {
    // Remove from all groups
    const groupsResp = await iamClient.send(new ListGroupsForUserCommand({ UserName: iamUsername }));
    for (const group of groupsResp.Groups || []) {
      await iamClient.send(new RemoveUserFromGroupCommand({ UserName: iamUsername, GroupName: group.GroupName! }));
    }

    // Delete all access keys
    const keysResp = await iamClient.send(new ListAccessKeysCommand({ UserName: iamUsername }));
    for (const key of keysResp.AccessKeyMetadata || []) {
      await iamClient.send(new DeleteAccessKeyCommand({ UserName: iamUsername, AccessKeyId: key.AccessKeyId! }));
    }

    // Delete the IAM user
    await iamClient.send(new DeleteUserCommand({ UserName: iamUsername }));
  } catch (err: any) {
    // If IAM user doesn't exist, continue with DynamoDB cleanup
    if (err.name !== "NoSuchEntityException") {
      console.warn("IAM cleanup error:", err.message);
    }
  }

  // Delete from DynamoDB
  const { DeleteCommand } = await import("@aws-sdk/lib-dynamodb");
  await getDocClient().send(
    new DeleteCommand({ TableName: USERS_TABLE, Key: { username } })
  );
}

/** Check if a user's session is still valid (user still exists) via Auth API */
export async function isSessionValid(username: string): Promise<boolean> {
  try {
    const resp = await fetch(`${AUTH_API_URL}/auth/validate-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    const data = await resp.json();
    return data.valid === true;
  } catch {
    // Network error — assume valid to avoid logout on connectivity issues
    return true;
  }
}

/** Check if user must change password */
export async function mustChangePassword(username: string): Promise<boolean> {
  const user = await getItem(USERS_TABLE, { username }) as PortalUser | undefined;
  return user?.mustChangePassword === true;
}

/** Generate a random temp password */
function generateTempPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$";
  let password = "";
  const array = new Uint8Array(12);
  crypto.getRandomValues(array);
  for (const byte of array) {
    password += chars[byte % chars.length];
  }
  return password;
}

/**
 * Reset a user's password to a temp password and flag for change on next login.
 * Returns the temp password so it can be emailed.
 */
export async function resetUserPassword(username: string): Promise<string> {
  const tempPassword = generateTempPassword();
  const hash = await hashPassword(tempPassword);
  await updateItem(USERS_TABLE, { username }, {
    passwordHash: hash,
    mustChangePassword: true,
  });
  return tempPassword;
}

/**
 * Send temp password via SES email.
 */
export async function sendTempPasswordEmail(
  email: string,
  username: string,
  tempPassword: string,
  senderEmail: string = "noreply@devportal.rmlittlebits.com"
): Promise<void> {
  const { SESClient, SendEmailCommand } = await import("@aws-sdk/client-ses");
  const ses = new SESClient({ region: "us-east-1" });

  await ses.send(new SendEmailCommand({
    Source: senderEmail,
    Destination: { ToAddresses: [email] },
    Message: {
      Subject: { Data: "POS DevPortal — Your Account Credentials" },
      Body: {
        Html: {
          Data: `
            <h2>Welcome to POS DevPortal</h2>
            <p>An account has been created for you.</p>
            <table style="margin: 1rem 0; font-family: monospace;">
              <tr><td style="padding: 0.25rem 1rem; font-weight: bold;">Username:</td><td>${username}</td></tr>
              <tr><td style="padding: 0.25rem 1rem; font-weight: bold;">Temporary Password:</td><td>${tempPassword}</td></tr>
            </table>
            <p><strong>You will be required to change your password on first login.</strong></p>
            <p style="color: #666; font-size: 0.85rem;">If you did not expect this email, please ignore it.</p>
          `,
        },
        Text: {
          Data: `POS DevPortal Account\n\nUsername: ${username}\nTemporary Password: ${tempPassword}\n\nYou will be required to change your password on first login.`,
        },
      },
    },
  }));
}

/** Change password via Auth API and clear the mustChangePassword flag in localStorage */
export async function changePassword(username: string, newPassword: string): Promise<void> {
  const resp = await fetch(`${AUTH_API_URL}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, newPassword }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data.error || "Failed to change password");
  }

  // Update localStorage session to clear mustChangePassword
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const session = JSON.parse(stored);
    session.mustChangePassword = false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }
}

// ============================
// Permissions
// ============================

export type Permission =
  | "pipeline:view"
  | "pipeline:trigger"
  | "pipeline:stop"
  | "pipeline:approve-frontend"
  | "pipeline:approve-backend"
  | "pipeline:rollback"
  | "pipeline:gates"
  | "versions:create"
  | "versions:promote-dev"
  | "versions:promote-beta"
  | "versions:promote-prod"
  | "features:manage"
  | "bugs:manage"
  | "admin:users";

const ROLE_PERMISSIONS: Record<PortalRole, Permission[]> = {
  admin: [
    "pipeline:view",
    "pipeline:trigger",
    "pipeline:stop",
    "pipeline:approve-frontend",
    "pipeline:approve-backend",
    "pipeline:rollback",
    "pipeline:gates",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "versions:promote-prod",
    "features:manage",
    "bugs:manage",
    "admin:users",
  ],
  authorizer: [
    "pipeline:view",
    "pipeline:approve-frontend",
    "pipeline:approve-backend",
    "pipeline:rollback",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "versions:promote-prod",
    "features:manage",
    "bugs:manage",
  ],
  user: [
    "pipeline:view",
    "pipeline:approve-frontend",
    "versions:create",
    "versions:promote-dev",
    "versions:promote-beta",
    "features:manage",
    "bugs:manage",
  ],
};

/** Check if a role has a specific permission */
export function hasPermission(role: PortalRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Get all permissions for a role */
export function getPermissions(role: PortalRole): Permission[] {
  return ROLE_PERMISSIONS[role];
}
